// Milestone create form. Title, description, parent rock, owner (defaults
// to the rock's primary owner on save), due date. Drillcut keeps this
// as its own form so a milestone can be raised standalone or from
// inside a Rock context.

import { useState } from "react";
import type { CreateSeed } from "./types";
import { FormSection, FormLabel, Hint, DC, twoCol } from "./primitives";
import { DcInput } from "./fields";
import { RichTextEditor } from "../../components/RichTextEditor";
import { PersonSelect, RockSelect } from "./selects";
import { Footer } from "./Footer";
import { tpApi } from "../../api";

interface MilestoneFormProps {
  seed?: CreateSeed;
  onClose: () => void;
  onCreated: () => void;
}

function parseDue(raw: string): Date | null {
  const t = raw.trim();
  if (!t) return null;
  const m = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [, y, mo, d] = m;
  return new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d), 17, 0, 0));
}

export function MilestoneForm({ seed, onClose, onCreated }: MilestoneFormProps) {
  const [title, setTitle] = useState(seed?.title ?? "");
  const [description, setDescription] = useState("");
  const [rockId, setRockId] = useState<string | null>(seed?.rockId ?? null);
  const [owner, setOwner] = useState<string | null>(null);
  const [due, setDue] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const canSubmit = title.trim().length > 0 && rockId !== null && due !== "" && !pending;
  const summary = rockId
    ? due
      ? `Linked Rock · due ${due}`
      : "Linked Rock"
    : "Pick a parent Rock";

  async function submit() {
    if (!rockId) return;
    const dueDate = parseDue(due);
    if (!dueDate) {
      setSubmitError("Pick a due date.");
      return;
    }
    setSubmitError(null);
    setPending(true);
    try {
      const milestone = await tpApi.createMilestone({
        rockId,
        title: title.trim(),
        dueAt: dueDate.toISOString(),
        description:
          description.trim() === "<p></p>" || description.trim() === ""
            ? undefined
            : description.trim(),
      });
      if (owner) {
        await tpApi.setPrimaryOwner("milestone", milestone.id, {
          subjectType: "user",
          subjectId: owner,
        });
      }
      onCreated();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Unknown error.");
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <div style={bodyScrollStyle}>
        <FormSection>
          <FormLabel required>Milestone title</FormLabel>
          <DcInput
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Vendor MSA signed"
          />
          <Hint>One visible checkpoint inside the parent Rock.</Hint>
        </FormSection>

        <FormSection>
          <FormLabel>Description</FormLabel>
          <RichTextEditor
            value={description}
            onChange={setDescription}
            placeholder="What does done look like?"
            minRows={3}
          />
        </FormSection>

        <FormSection>
          <FormLabel required>Parent Rock</FormLabel>
          <RockSelect value={rockId} onChange={setRockId} />
          <Hint>Milestones always live inside a Rock — pick which one.</Hint>
        </FormSection>

        <div style={twoCol}>
          <FormSection>
            <FormLabel>Owner</FormLabel>
            <PersonSelect value={owner} onChange={setOwner} />
            <Hint>Defaults to you if left blank.</Hint>
          </FormSection>
          <FormSection>
            <FormLabel required>Due</FormLabel>
            <DcInput
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
            />
          </FormSection>
        </div>
      </div>
      <Footer
        color={DC.green}
        primary="Create milestone"
        disabled={!canSubmit}
        onCancel={onClose}
        onSubmit={submit}
        error={submitError}
        summary={summary}
      />
    </>
  );
}

const bodyScrollStyle = {
  flex: 1,
  overflowY: "auto" as const,
  padding: "16px 22px 20px",
  display: "flex",
  flexDirection: "column" as const,
};
